import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Building2, Users, Shield, CheckCircle } from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';

const OrganizationSwitcher = () => {
  const { currentTenant, availableTenants, switchTenant, loading } = useTenant();
  const [isOpen, setIsOpen] = useState(false);

  const handleTenantSwitch = async (tenant) => {
    await switchTenant(tenant);
    setIsOpen(false);
  };

  const getTenantIcon = (tier) => {
    switch (tier) {
      case 'enterprise':
        return <Building2 className="w-4 h-4" />;
      case 'premium':
        return <Shield className="w-4 h-4" />;
      default:
        return <Users className="w-4 h-4" />;
    }
  };

  const getTenantColor = (tier) => {
    switch (tier) {
      case 'enterprise':
        return 'text-blue-500';
      case 'premium':
        return 'text-purple-500';
      default:
        return 'text-gray-500';
    }
  };

  if (!currentTenant) {
    return (
      <div className="flex items-center space-x-2 px-3 py-2 bg-gray-800 rounded-lg">
        <div className="w-4 h-4 bg-gray-600 rounded animate-pulse"></div>
        <span className="text-sm text-gray-400">Loading...</span>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Current Tenant Display */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-3 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        <div className="flex items-center space-x-2">
          <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getTenantColor(currentTenant.tier)} bg-opacity-20 flex items-center justify-center`}>
            {getTenantIcon(currentTenant.tier)}
          </div>
          <div className="text-left">
            <div className="text-sm font-medium text-white">
              {currentTenant.custom_branding?.company_name || currentTenant.name}
            </div>
            <div className="text-xs text-gray-400 capitalize">
              {currentTenant.tier} Plan
            </div>
          </div>
        </div>
        <ChevronDown 
          className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 right-0 mt-2 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 min-w-80"
          >
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-700">
              <h3 className="text-sm font-medium text-white">Switch Organization</h3>
              <p className="text-xs text-gray-400 mt-1">
                Select a different organization to manage
              </p>
            </div>

            {/* Tenant List */}
            <div className="py-2 max-h-64 overflow-y-auto">
              {availableTenants.map((tenant) => (
                <button
                  key={tenant.id}
                  onClick={() => handleTenantSwitch(tenant)}
                  disabled={loading || tenant.id === currentTenant.id}
                  className={`w-full px-4 py-3 text-left hover:bg-gray-700 transition-colors duration-200 focus:outline-none focus:bg-gray-700 ${
                    tenant.id === currentTenant.id ? 'bg-blue-900 bg-opacity-50' : ''
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getTenantColor(tenant.tier)} bg-opacity-20 flex items-center justify-center`}>
                      {getTenantIcon(tenant.tier)}
                    </div>
                    <div className="flex-1 text-left">
                      <div className="text-sm font-medium text-white">
                        {tenant.custom_branding?.company_name || tenant.name}
                      </div>
                      <div className="text-xs text-gray-400">
                        {tenant.max_locations} locations • {tenant.max_users} users
                      </div>
                    </div>
                    {tenant.id === currentTenant.id && (
                      <CheckCircle className="w-4 h-4 text-blue-400" />
                    )}
                  </div>
                </button>
              ))}
            </div>

            {/* Footer */}
            <div className="px-4 py-3 border-t border-gray-700 bg-gray-900 rounded-b-lg">
              <div className="text-xs text-gray-400">
                <div className="flex items-center justify-between">
                  <span>Current Plan:</span>
                  <span className="capitalize font-medium text-white">
                    {currentTenant.tier}
                  </span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span>API Calls:</span>
                  <span className="text-white">Unlimited</span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default OrganizationSwitcher;
